import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import '../../style/speakSearch.css'; 
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const UserWiseCallCenterSurveyMember = () => {
    // --- State ---
    const [voters, setVoters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState(""); 
    const [filterStatus, setFilterStatus] = useState("ALL");
    
    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    // --- Hooks & Context ---
    const navigate = useNavigate();
    const location = useLocation();
    const { appId, language } = useAppId();
    const lang = language || 'en';

    // Retrieve user_id from router state passed from the dashboard
    const passedUserId = location.state?.user_id || "";

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Surveyed Voters",
            subtitlePrefix: "Voters surveyed by User ID:",
            searchPlaceholder: "Search voter by name or mobile...",
            loadingData: "Loading Data...",
            noData: "No voters found matching",
            
            // Labels
            fatherHusband: "F/H :",
            serialNumber: "Sr No. :",
            mobile: "Mobile :",
            idCard: "ID Card :",
            boothNo: "Booth No :",
            houseNo: "House No :",
            pollingLocation: "Voting Loc :",
            note: "Note :",
            n_a: "N/A",

            // Statuses & Filter
            allStatuses: "All Statuses",
            positive: "Positive",
            negative: "Negative",
            doubtful: "Doubtful",
            cantSay: "Can't Say",
            notReceived: "Not Received",
            wrongMobile: "Wrong Mobile",

            // Actions
            call: "Call",
            text: "Text",
            slip: "Slip",
            prachar: "Prachar",
            print: "Print",
            family: "Family"
        },
        hi: {
            pageTitle: "सर्वेक्षित मतदाता",
            subtitlePrefix: "उपयोगकर्ता आईडी द्वारा सर्वेक्षित मतदाता:",
            searchPlaceholder: "नाम या मोबाइल से खोजें...",
            loadingData: "डेटा लोड हो रहा है...",
            noData: "कोई मतदाता नहीं मिला",
            
            fatherHusband: "पिता/पति :",
            serialNumber: "क्रमांक :",
            mobile: "मोबाइल :",
            idCard: "पहचान पत्र :",
            boothNo: "बूथ नं :",
            houseNo: "घर नं :",
            pollingLocation: "मतदान स्थान :",
            note: "नोट :",
            n_a: "N/A",

            allStatuses: "सभी स्थितियां",
            positive: "सकारात्मक",
            negative: "नकारात्मक",
            doubtful: "संदिग्ध",
            cantSay: "कह नहीं सकते",
            notReceived: "प्राप्त नहीं",
            wrongMobile: "गलत मोबाइल",

            call: "कॉल",
            text: "मैसेज",
            slip: "पर्ची",
            prachar: "प्रचार",
            print: "प्रिंट",
            family: "परिवार"
        },
        gu: {
            pageTitle: "સર્વેક્ષણ કરેલ મતદારો",
            subtitlePrefix: "વપરાશકર્તા ID દ્વારા સર્વેક્ષણ કરેલ મતદારો:",
            searchPlaceholder: "નામ અથવા મોબાઇલથી શોધો...",
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            noData: "કોઈ મતદાર મળ્યા નથી",
            
            fatherHusband: "પિતા/પતિ :",
            serialNumber: "ક્રમાંક :",
            mobile: "મોબાઇલ :",
            idCard: "ઓળખ કાર્ડ :",
            boothNo: "બૂથ નં :",
            houseNo: "ઘર નં :",
            pollingLocation: "મતદાન સ્થળ :",
            note: "નોંધ :",
            n_a: "N/A",

            allStatuses: "તમામ સ્થિતિઓ",
            positive: "હકારાત્મક",
            negative: "નકારાત્મક",
            doubtful: "શંકાસ્પદ",
            cantSay: "કહી શકતો નથી",
            notReceived: "પ્રાપ્ત થયું નથી",
            wrongMobile: "ખોટો મોબાઈલ",

            call: "કૉલ",
            text: "સંદેશ",
            slip: "સ્લિપ",
            prachar: "પ્રચાર",
            print: "પ્રિંટ",
            family: "કુટુંબ"
        }
    };

    const txt = t[lang];

    // --- API Fetch Logic ---
    const fetchVoters = async () => {
        if (!appId || !passedUserId) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_user_wise_call_center_survey_voter`,
                { 
                    app_id: appId,
                    user_id: passedUserId 
                }
            );

            if (response.data.success && response.data.data && Array.isArray(response.data.data.result)) {
                setVoters(response.data.data.result);
            } else {
                setVoters([]);
            }
        } catch (error) {
            console.error("Error fetching surveyed voters by user:", error);
            setVoters([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!passedUserId) {
            navigate(-1); 
        } else {
            fetchVoters();
        }
    }, [appId, passedUserId]);

    // --- Filter Logic ---
    const filteredVoters = voters.filter((voter) => {
        let matchesSearch = true;
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            const fullName = `${voter.eng_f_name} ${voter.eng_surname}`.toLowerCase();
            matchesSearch = fullName.includes(lowerSearch) || String(voter.contact_no).includes(lowerSearch);
        }
        
        let matchesStatus = true;
        if (filterStatus !== "ALL") {
            matchesStatus = String(voter.voter_status || "").toLowerCase() === filterStatus;
        }

        return matchesSearch && matchesStatus;
    });

    // --- Pagination Math ---
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredVoters.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredVoters.length / itemsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisibleButtons = 5;
        if (totalPages <= maxVisibleButtons) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            pageNumbers.push(1);
            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);
            if (currentPage <= 3) endPage = 4;
            if (currentPage >= totalPages - 2) startPage = totalPages - 3;
            if (startPage > 2) pageNumbers.push('...');
            for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
            if (endPage < totalPages - 1) pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };

    // Helper for Status Badge Configuration
    const getStatusConfig = (statusCode) => {
        const s = String(statusCode || "").toLowerCase();
        switch(s) {
            case 'p': return { label: txt.positive, className: 'status-p' };
            case 'n': return { label: txt.negative, className: 'status-n' };
            case 'd': return { label: txt.doubtful, className: 'status-d' };
            case 'c': return { label: txt.cantSay, className: 'status-c' };
            case 'nr': return { label: txt.notReceived, className: 'status-nr' };
            case 'wm': return { label: txt.wrongMobile, className: 'status-wm' };
            default: return { label: statusCode || "N/A", className: 'status-default' };
        }
    };

    // --- AVATAR HELPERS ---
    const getInitials = (name) => {
        if (!name || name.trim() === "") return "U";
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.charAt(0).toUpperCase();
    };

    const getAvatarGradient = (name) => {
        const gradients = [
            'linear-gradient(135deg, #3b82f6 0%, #2dd4bf 100%)', // Blue to Teal
            'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)', // Orange to Red
            'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)', // Purple to Pink
            'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)', // Green to Blue
            'linear-gradient(135deg, #f43f5e 0%, #8b5cf6 100%)', // Rose to Purple
            'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)'  // Sky to Indigo
        ];
        let hash = 0;
        const str = name || "Unknown";
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        return gradients[Math.abs(hash) % gradients.length];
    };

    // Helper for WhatsApp Share
    const handleWhatsAppShare = async (voter) => {
        if (!voter.contact_no) {
            alert("Mobile number not available.");
            return;
        }

        try {
            const response = await axios.post(`${BASE_URL}/api/get_receipt_in_all_family_for_wtsp`, {
                app_id: appId,
                voter_id: voter.id,
                user_id: appId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const familyMembers = response.data.data.result;
                let message = "";

                familyMembers.forEach((member, index) => {
                    message += `${member.first_detail.replace(/\s+/g, ' ')}\n`;
                    message += `${member.full_name}\n`;
                    message += `${member.middle_name}\n`;
                    message += `${member.sex_age.replace('स्री', 'स्त्री')}\n`;
                    message += `${member.idcard_no}\n\n`;
                    message += `मतदान स्थल :\n`;
                    message += `${member.eng_polling_location}\n\n`;

                    if (index < familyMembers.length - 1) {
                        message += `----------------------------------------\n\n`;
                    }
                });

                const encodedMessage = encodeURIComponent(message);
                let mobile = voter.contact_no.replace(/\D/g, '');
                if (mobile.length === 10) mobile = '91' + mobile;
                window.open(`https://wa.me/${mobile}?text=${encodedMessage}`, '_blank');

            } else {
                alert("Could not fetch family details.");
            }
        } catch (error) {
            console.error("Error generating WhatsApp slip:", error);
        }
    };

    return (
        <div className='app-container p-4'>

            {/* Header */}
            <Header title={txt.pageTitle} />

            {/* --- PROFESSIONAL FILTER CONTAINER --- */}
            <div className="filter-container">
                <div className="search-wrapper">
                    <svg className="search-icon-main" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        className="search-input"
                        placeholder={txt.searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1); 
                        }}
                    />
                </div>

                {/* Dropdown Filter */}
                <div className="status-filter-wrapper">
                    <select 
                        className="status-filter-select"
                        value={filterStatus}
                        onChange={(e) => {
                            setFilterStatus(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="ALL">{txt.allStatuses}</option>
                        <option value="p">{txt.positive}</option>
                        <option value="n">{txt.negative}</option>
                        <option value="d">{txt.doubtful}</option>
                        <option value="c">{txt.cantSay}</option>
                        <option value="nr">{txt.notReceived}</option>
                        <option value="wm">{txt.wrongMobile}</option>
                    </select>
                </div>
            </div>

            {/* --- Loading State --- */}
            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
            )}

            {/* No Data State */}
            {!loading && filteredVoters.length === 0 && (
                <div className="row mt-3">
                    <div className="col-12 text-center text-muted p-5 empty-state">
                        <div className="empty-icon">👥</div>
                        <p>{txt.noData} {searchTerm && `"${searchTerm}"`}</p>
                    </div>
                </div>
            )}

            {/* --- Results Grid --- */}
            <div className="row g-4 mt-1 pb-5">
                {!loading && currentItems.map((voter, index) => {
                    const statusObj = getStatusConfig(voter.voter_status);
                    // Filter out "-" from names
                    const fName = voter.eng_f_name && voter.eng_f_name !== '-' ? voter.eng_f_name : '';
                    const sName = voter.eng_surname && voter.eng_surname !== '-' ? voter.eng_surname : '';
                    const fullName = `${fName} ${sName}`.trim();

                    return (
                        <div className="col-12 col-md-6 col-lg-4" key={voter.id || index}>
                            <div className="voter-card-clean h-100">
                                
                                {/* Header with Status Badge */}
                                <div className="voter-header d-flex justify-content-between align-items-center pb-3 mb-3 border-bottom">
                                    <div className="d-flex align-items-center gap-3">
                                        <span className="serial-number">{voter.slnoinpart || (index + 1 + indexOfFirstItem)}.</span>
                                        <span className="voter-name-bold m-0">{fullName || "Unknown Name"}</span>
                                    </div>
                                    <span className={`status-pill ${statusObj.className}`}>
                                        {statusObj.label}
                                    </span>
                                </div>

                                {/* Body Information */}
                                <div className="voter-body position-relative">
                                    <div className="map-icon-wrapper">
                                        <img src="https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg" alt="Map" width="15" />
                                    </div>
                                    <div className="info-row"><span className="label">{txt.fatherHusband}</span><span className="value">{voter.eng_m_name && voter.eng_m_name !== '-' ? voter.eng_m_name : txt.n_a}</span></div>
                                    <div className="info-row"><span className="label">{txt.serialNumber}</span><span className="value">{voter.serial_no || voter.slnoinpart || index + 1}</span></div>
                                    <div className="info-row"><span className="label">{txt.mobile}</span><span className="value mobile-value">{voter.contact_no || txt.n_a}</span></div>
                                    <div className="info-row"><span className="label">{txt.idCard}</span><span className="value">{voter.idcard_no || txt.n_a}</span></div>
                                    <div className="info-row"><span className="label">{txt.boothNo}</span><span className="value">{voter.part_no || voter.booth_no || txt.n_a}</span></div>
                                    <div className="info-row"><span className="label">{txt.houseNo}</span><span className="value">{voter.eng_house_no || txt.n_a}</span></div>
                                    <div className="info-row"><span className="label">{txt.pollingLocation}</span><span className="value">{voter.eng_polling_location || "-"}</span></div>
                                    
                                    {voter.note && (
                                        <div className="info-row mt-2 p-2 bg-light rounded border"><span className="label">{txt.note}</span><span className="value text-muted">{voter.note}</span></div>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="voter-actions mt-auto pt-3 border-top">
                                    <ActionButton icon="📞" color="#0ea5e9" label={txt.call} link={`tel:${voter.contact_no}`} />
                                    <ActionButton icon="✉️" color="#f59e0b" label={txt.text} link={`sms:${voter.contact_no}`} />
                                    <button onClick={() => handleWhatsAppShare(voter)} className="action-btn-wrapper" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                        <div className="circle-btn" style={{ backgroundColor: '#22c55e' }}>💬</div>
                                        <span className="btn-label">{txt.slip}</span>
                                    </button>
                                    <ActionButton icon="👤" color="#ef4444" label={txt.prachar} />
                                    <ActionButton icon="🖨" color="#f97316" label={txt.print} onClick={() => navigate('/printer', { state: { voter_id: voter.id } })} />
                                    <ActionButton icon="👨‍👩‍👧" color="#fbbf24" label={txt.family} />
                                </div>

                            </div>
                        </div>
                    );
                })}
            </div>

            {/* --- PAGINATION CONTROLS --- */}
            {!loading && filteredVoters.length > itemsPerPage && (
                <div className="pagination-wrapper">
                    <div className="pagination-content">
                        <button className="pagination-nav-btn" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        <div className="page-numbers">
                            {getPageNumbers().map((number, index) => (
                                <React.Fragment key={index}>
                                    {number === '...' ? (<span className="page-dots">...</span>) : (
                                        <button className={`page-num-btn ${currentPage === number ? 'active' : ''}`} onClick={() => paginate(number)}>{number}</button>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                        <button className="pagination-nav-btn" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>
                </div>
            )}

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* Subtitle Styles */
                .subtitle-container { margin-top: 8px; margin-bottom: 20px; position: relative; }
                .subtitle-text { font-size: 15px; color: #334155; margin: 0 0 10px 0; font-weight: 600; }
                .subtitle-container::after { content: ""; display: block; width: 60px; height: 3px; background: linear-gradient(to right, #2563eb, #60a5fa); border-radius: 3px; margin-top: 4px; }

                /* --- SEARCH & FILTER BAR STYLE --- */
                .filter-container { 
                    display: flex; 
                    justify-content: space-between; 
                    align-items: center; 
                    gap: 16px; 
                    margin-bottom: 24px; 
                }
                .search-wrapper { 
                    position: relative; 
                    flex: 1; 
                    max-width: 450px; 
                }
                .search-icon-main { 
                    position: absolute; 
                    left: 14px; 
                    top: 50%; 
                    transform: translateY(-50%); 
                    color: #94a3b8; 
                    width: 20px; 
                    height: 20px; 
                    z-index: 1;
                }
                .search-input { 
                    width: 100%; 
                    padding: 12px 16px 12px 42px; 
                    border: 1px solid #e2e8f0; 
                    border-radius: 10px; 
                    font-size: 14px; 
                    color: #1e293b; 
                    outline: none; 
                    transition: all 0.2s ease; 
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05); 
                }
                .search-input:focus { 
                    border-color: #2563eb; 
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); 
                }

                /* --- DROPDOWN FILTER STYLE --- */
                .status-filter-wrapper {
                    flex-shrink: 0;
                }
                .status-filter-select {
                    padding: 12px 36px 12px 16px;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    font-size: 14px;
                    color: #1e293b;
                    background-color: white;
                    outline: none;
                    cursor: pointer;
                    appearance: none;
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
                    background-repeat: no-repeat;
                    background-position: right 12px center;
                    background-size: 16px;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                }
                .status-filter-select:focus {
                    border-color: #2563eb;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
                }

                /* Stylish Avatar */
                .voter-avatar-stylish {
                    width: 42px;
                    height: 42px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: 700;
                    font-size: 16px;
                    text-shadow: 0 1px 2px rgba(0,0,0,0.2);
                    box-shadow: 0 3px 6px rgba(0,0,0,0.1), inset 0 -2px 4px rgba(0,0,0,0.1);
                    border: 2px solid white;
                    flex-shrink: 0;
                }

                /* Status Pills */
                .status-pill {
                    padding: 4px 10px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .status-p { background-color: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
                .status-n { background-color: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }
                .status-d { background-color: #fef3c7; color: #b45309; border: 1px solid #fde68a; }
                .status-c { background-color: #e0e7ff; color: #3730a3; border: 1px solid #c7d2fe; }
                .status-nr { background-color: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
                .status-wm { background-color: #e2e8f0; color: #334155; border: 1px solid #cbd5e1; }
                .status-default { background-color: #f8fafc; color: #94a3b8; border: 1px solid #f1f5f9; }

                .empty-state { background: white; border-radius: 20px; border: 2px dashed #e2e8f0; }
                .empty-icon { font-size: 3rem; margin-bottom: 10px; opacity: 0.5; }

                /* Loading specific overrides */
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 40vh; width: 100%; }
                .spinner { width: 40px; height: 40px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 12px; }
                .loader-text { color: #6B7280; font-weight: 500; }
                
                /* Pagination Styles */
                .pagination-wrapper { display: flex; justify-content: center; padding: 20px 0; position: sticky; bottom: 20px; z-index: 50; }
                .pagination-content { background: white; border: 1px solid #E2E8F0; border-radius: 50px; padding: 6px 12px; display: flex; align-items: center; gap: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); }
                .pagination-nav-btn { display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 50%; background: transparent; border: none; color: #2563EB; cursor: pointer; }
                .pagination-nav-btn:disabled { color: #CBD5E1; cursor: not-allowed; }
                .page-numbers { display: flex; gap: 4px; align-items: center; }
                .page-num-btn { width: 30px; height: 30px; border-radius: 50%; border: none; background: transparent; color: #475569; font-weight: 600; font-size: 13px; cursor: pointer; }
                .page-num-btn.active { background-color: #2563EB; color: white; }

                @media (max-width: 640px) {
                    .filter-container { flex-direction: column; align-items: stretch; gap: 12px; }
                    .search-wrapper { max-width: 100%; }
                    .status-filter-wrapper { width: 100%; }
                    .status-filter-select { width: 100%; }
                }
            `}</style>
        </div>
    );
};

// Reusable ActionButton
const ActionButton = ({ icon, color, label, link, onClick }) => {
    if (onClick) {
        return (
            <button onClick={onClick} className="action-btn-wrapper" style={{ textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
                <span className="btn-label">{label}</span>
            </button>
        );
    }
    
    return (
        <a href={link || "#"} className="action-btn-wrapper" style={{ textDecoration: 'none' }} target="_blank" rel="noreferrer">
            <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
            <span className="btn-label">{label}</span>
        </a>
    );
};

export default UserWiseCallCenterSurveyMember;